import { useReveal } from "@/hooks/useReveal";
import { drinksMenu, foodMenu, business } from "@/data/siteData";

function MenuRow({ item, idx, testidPrefix }) {
  return (
    <li
      data-testid={`${testidPrefix}-item-${idx}`}
      className="reveal py-5 md:py-6 border-b border-[#132A1C]/15 last:border-b-0"
    >
      <div className="flex items-baseline gap-3 flex-wrap">
        <h4 className="font-serif-display text-xl md:text-2xl text-[#132A1C] tracking-tight">
          {item.name}
        </h4>
        <span
          className="flex-1 border-b border-dotted border-[#132A1C]/25 translate-y-[-6px] min-w-[1rem]"
          aria-hidden
        />
        <span className="font-serif-display text-xl md:text-2xl text-[#132A1C] tabular-nums">
          €{item.price}
        </span>
      </div>
      <p className="mt-1.5 text-[#2C4C3B] max-w-xl leading-relaxed text-[14.5px]">
        <span className="text-[11px] tracking-[0.2em] uppercase text-[#D4A373] mr-3 align-middle">
          {item.vn}
        </span>
        {item.desc}
      </p>
    </li>
  );
}

function FeaturedCard({ item, idx }) {
  return (
    <div
      data-testid={`featured-drink-${idx}`}
      className="reveal group bg-[#F4F0EA] border border-[#132A1C]/10 flex flex-col"
    >
      <div className="relative overflow-hidden aspect-[4/5] bg-[#EBE4D8]">
        <img
          src={item.image}
          alt={`${item.name} at Cafe Anh Helsinki`}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
        />
      </div>
      <div className="p-5 md:p-6 flex-1 flex flex-col">
        <div className="flex items-baseline justify-between gap-3">
          <h4 className="font-serif-display text-xl md:text-2xl text-[#132A1C] tracking-tight">
            {item.name}
          </h4>
          <span className="font-serif-display text-lg md:text-xl text-[#132A1C] tabular-nums shrink-0">
            €{item.price}
          </span>
        </div>
        <span className="mt-1 text-[11px] tracking-[0.18em] uppercase text-[#D4A373]">
          {item.vn}
        </span>
        <p className="mt-3 text-[#2C4C3B] text-[14px] leading-relaxed">
          {item.desc}
        </p>
      </div>
    </div>
  );
}

export default function MenuSection() {
  const ref = useReveal();
  const featured = drinksMenu.filter((d) => d.featured);
  return (
    <section
      id="menu"
      ref={ref}
      data-testid="menu-section"
      className="relative py-20 md:py-28 bg-[#F4F0EA]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 mb-12 md:mb-16">
          <div className="lg:col-span-7">
            <p className="reveal text-[11px] tracking-[0.32em] uppercase text-[#5A7365] mb-5">
              Featured · Vietnamese coffee
            </p>
            <h2 className="reveal font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#132A1C] leading-[1.08] tracking-tight">
              The coffee we're <span className="italic">known</span> for.
            </h2>
          </div>
          <p className="reveal lg:col-span-5 text-[#2C4C3B] text-base md:text-lg leading-relaxed self-end">
            Strong, sweet and unmistakably Vietnamese. Most drinks land between{" "}
            {business.priceRange} — pick a hot phin in winter, an iced bạc xỉu in summer.
          </p>
        </div>

        {/* Featured drinks grid */}
        <div
          data-testid="featured-drinks-grid"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6"
        >
          {featured.map((item, i) => (
            <FeaturedCard key={item.name} item={item} idx={i} />
          ))}
        </div>

        {/* Full menu lists */}
        <div className="mt-16 md:mt-24 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          <div>
            <div className="reveal flex items-baseline justify-between border-b border-[#132A1C]/30 pb-4 mb-2">
              <h3 className="font-serif-display text-2xl md:text-3xl text-[#132A1C]">Drinks</h3>
              <span className="text-[11px] tracking-[0.2em] uppercase text-[#5A7365]">Hot · Iced</span>
            </div>
            <ul data-testid="drinks-list">
              {drinksMenu.map((item, i) => (
                <MenuRow key={item.name} item={item} idx={i} testidPrefix="drink" />
              ))}
            </ul>
          </div>
          <div>
            <div className="reveal flex items-baseline justify-between border-b border-[#132A1C]/30 pb-4 mb-2">
              <h3 className="font-serif-display text-2xl md:text-3xl text-[#132A1C]">Food</h3>
              <span className="text-[11px] tracking-[0.2em] uppercase text-[#5A7365]">Daily</span>
            </div>
            <ul data-testid="food-list">
              {foodMenu.map((item, i) => (
                <MenuRow key={item.name} item={item} idx={i} testidPrefix="food" />
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
